/**
 * AB#21836 — Desk messages: a lead writes a message once; the people in its
 * audience see it full-screen when a condition on their own Needs Attention
 * queue is met, and can re-read it from the Messages bell.
 *
 * Everything that decides is on the server (audience, condition, frequency,
 * wording, buttons). The browser shows the card it is given, and reports what
 * the person did with it.
 */
import { ref } from 'vue';
import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';

export interface DeskFeatures {
  desk_messages_manage?: boolean;
  desk_messages_receive?: boolean;
}
export const deskMessagesManage = (f?: DeskFeatures | null): boolean =>
  f?.desk_messages_manage === true;
export const deskMessagesReceive = (f?: DeskFeatures | null): boolean =>
  f?.desk_messages_receive === true;

export type Tone = 'info' | 'reminder' | 'urgent';
export type Frequency = 'daily' | 'every_visit' | 'until_dismissed';
export type Display = 'fullscreen' | 'bell';
export type EventName =
  'shown' | 'acted' | 'later' | 'hidden' | 'snoozed' | 'suppressed' | 'opened_from_bell';

/**
 * AB#21910 — a desk message is either a rule someone wrote, or the weekly
 * digest ELSA generates. The digest row is managed here like any other message
 * (who, which day and time, on/off) but its text is composed on the day.
 */
export type DeskKind = 'rule' | 'weekly_digest';

export interface DigestStatus {
  rule_id: number;
  enabled: boolean;
  /** ISO weekday, 1 = Monday. */
  day: number;
  time: string;
  timezone: string;
  next_run: string | null;
  last_sent: string | null;
  recipients: number;
}

export interface DeskRule {
  id: number;
  kind?: DeskKind;
  name: string;
  message: string;
  tone: Tone;
  condition: string;
  condition_label?: string;
  operator: 'gt' | 'gte' | 'eq';
  threshold: number;
  frequency: Frequency;
  display: Display;
  audience_role_ids: number[];
  audience_user_ids: number[];
  window_days: number[];
  window_from: string | null;
  window_to: string | null;
  active_from: string | null;
  active_to: string | null;
  enabled: boolean;
  author?: { id: number; name: string; avatar_url?: string | null };
  stats?: { shown: number; acted: number; hidden: number };
  can_edit?: boolean;
}

export interface DeskOrder {
  order_id: number;
  order_item_id?: number | null;
  order_no: string | null;
  /** "#28100", or "Order number pending" (AB#21886). */
  order_label?: string;
  company: string;
  headline: string;
  band: string;
}

export interface DeskCard {
  instance_key: string;
  headline: string;
  text: string;
  tone: Tone;
  /** `agent: 'elsa'` marks a message ELSA wrote (AB#21906 — the weekly digest). */
  from: { name: string; initials: string; avatar_url?: string | null; agent?: 'elsa' | null };
  rule: { id: number; name: string };
  orders: DeskOrder[];
  primary: {
    label: string;
    action: 'queue' | 'order' | 'team' | 'development' | 'development_request' | 'development_notice';
    /** AB#22092 — a product development record; AB#22639 — the step and comment it opens on. */
    development_id?: number;
    step?: number;
    comment_id?: number;
    /** AB#22169 — a request sent to Product development, and which page shows it. */
    request_id?: number;
    view?: 'review' | 'mine';
    /** AB#22173 — a Product development notice to acknowledge. */
    notice_id?: number;
    order_id?: number;
    order_item_id?: number | null;
    order_no?: string | null;
  };
  secondary: { label: string; action: 'later' | 'snooze' };
  can_hide_today: boolean;
  metrics?: Record<string, number>;
  also_due?: number;
  test?: boolean;
}

/** Display names for the audience ids on the listed rules, keyed by id. */
export interface AudienceNames {
  roles: Record<string, string>;
  people: Record<string, string>;
}

export interface Reach {
  roles: { id: number; name: string; receives: boolean; people: number }[];
  people: { id: number; name: string; role: string }[];
}

export interface InboxItem {
  instance_key: string;
  rule_id: number;
  at: string;
  read: boolean;
  card: DeskCard;
}

export interface ActivityRow {
  id: number;
  user: { id: number; name: string };
  event: string;
  reason: string | null;
  instance_key: string;
  at: string;
  context: Record<string, unknown>;
}

export const isDigest = (r?: Pick<DeskRule, 'kind'> | null): boolean => r?.kind === 'weekly_digest';

export const emptyRule = (): Omit<DeskRule, 'id'> & { id?: number } => ({
  name: '',
  message:
    'Good morning {first_name} — you have {count} orders needing attention today. {urgent_count} of them are urgent. Please clear the urgent ones first.',
  tone: 'reminder',
  condition: 'na_total',
  operator: 'gt',
  threshold: 5,
  frequency: 'daily',
  display: 'fullscreen',
  audience_role_ids: [],
  audience_user_ids: [],
  window_days: [],
  window_from: null,
  window_to: null,
  active_from: null,
  active_to: null,
  enabled: true,
});

const result = <T>(data: unknown): T => (data as { result: T }).result;

/* ------------------------------------------------------------ senders */
export const useDeskMessageAdmin = () => {
  const rules = ref<DeskRule[]>([]);
  const totals = ref({ active: 0, shown: 0, acted: 0, hidden: 0 });
  const conditions = ref<{ key: string; label: string }[]>([]);
  const operators = ref<Record<string, string>>({});
  const placeholders = ref<string[]>([]);
  const reach = ref<Reach>({ roles: [], people: [] });
  /* Names of every role and person the listed rules address, from the list
     endpoint. Reach decides who you may ADD; it must not decide whether you
     can read who a rule already goes to. */
  const names = ref<AudienceNames>({ roles: {}, people: {} });
  const digest = ref<DigestStatus | null>(null);
  const loading = ref(false);

  const load = async (): Promise<void> => {
    loading.value = true;
    try {
      const [{ data }, r] = await Promise.all([
        http.get('desk-messages'),
        http.get('desk-messages/reach'),
      ]);
      const res = result<{
        rules: DeskRule[];
        totals: typeof totals.value;
        conditions: { key: string; label: string }[];
        operators: Record<string, string>;
        placeholders: string[];
        digest?: DigestStatus | null;
        names?: AudienceNames;
      }>(data);
      names.value = { roles: res.names?.roles ?? {}, people: res.names?.people ?? {} };
      rules.value = res.rules;
      digest.value = res.digest ?? null;
      totals.value = res.totals;
      conditions.value = res.conditions;
      operators.value = res.operators;
      placeholders.value = res.placeholders;
      reach.value = result<Reach>(r.data);
    } catch (e) {
      handleResponseException(e as never);
    } finally {
      loading.value = false;
    }
  };

  const save = async (rule: Partial<DeskRule>): Promise<DeskRule | null> => {
    try {
      const { data } = rule.id
        ? await http.put(`desk-messages/${rule.id}`, rule)
        : await http.post('desk-messages', rule);
      if (data?.success === false) {
        handleResponseException({ isAxiosError: true, response: { status: 422, data } } as never);
        return null;
      }
      return result<DeskRule>(data);
    } catch (e) {
      handleResponseException(e as never);
      return null;
    }
  };

  const remove = async (id: number): Promise<boolean> => {
    try {
      await http.delete(`desk-messages/${id}`);
      return true;
    } catch (e) {
      handleResponseException(e as never);
      return false;
    }
  };

  const activity = async (id: number): Promise<ActivityRow[]> => {
    try {
      const { data } = await http.get(`desk-messages/${id}/activity`);
      return result<{ events: ActivityRow[] }>(data).events;
    } catch (e) {
      handleResponseException(e as never);
      return [];
    }
  };

  const test = async (id: number): Promise<DeskCard | null> => {
    try {
      const { data } = await http.post(`desk-messages/${id}/test`);
      return result<DeskCard>(data);
    } catch (e) {
      handleResponseException(e as never);
      return null;
    }
  };

  return {
    rules,
    totals,
    conditions,
    operators,
    placeholders,
    reach,
    names,
    digest,
    loading,
    load,
    save,
    remove,
    activity,
    test,
  };
};

/* ---------------------------------------------------------- receivers */
export const fetchPending = async (): Promise<DeskCard | null> => {
  try {
    const { data } = await http.get('desk-messages/pending');
    return result<{ message: DeskCard | null }>(data).message;
  } catch {
    return null; // a failed poll is silence, never a toast
  }
};

export const fetchInbox = async (): Promise<{ items: InboxItem[]; unread: number }> => {
  try {
    const { data } = await http.get('desk-messages/inbox');
    return result<{ items: InboxItem[]; unread: number }>(data);
  } catch {
    return { items: [], unread: 0 };
  }
};

export const recordEvent = async (
  card: Pick<DeskCard, 'rule' | 'instance_key'>,
  event: EventName,
  extra: { reason?: string; context?: Record<string, unknown> } = {}
): Promise<void> => {
  try {
    await http.post('desk-messages/events', {
      desk_message_id: card.rule.id,
      instance_key: card.instance_key,
      event,
      reason: extra.reason,
      context: extra.context,
    });
  } catch {
    /* recording must never get in the person's way */
  }
};

/**
 * Where "Open #order" and an order card go. The dashboard opens one order only
 * from the full deep link the Needs Attention page uses (order_id +
 * order_item_id + order_no); with less it shows the plain listing. And it
 * reads that link once, on mount — so from the dashboard itself we reload
 * the page at the new URL instead of pushing a query it would ignore.
 */
export const deskOrderTarget = (o: {
  order_id: number;
  order_item_id?: number | null;
  order_no?: string | null;
}): { name: string; query: Record<string, string> } => ({
  name: 'dashboard',
  query: {
    order_id: String(o.order_id),
    ...(o.order_item_id ? { order_item_id: String(o.order_item_id) } : {}),
    ...(o.order_no ? { order_no: String(o.order_no) } : {}),
  },
});

/** Where a card's primary button goes (AB#21906 adds `team`). */
export const deskPrimaryTarget = (
  primary: DeskCard['primary']
): { name: string; query?: Record<string, string> } => {
  if (primary.action === 'order' && primary.order_id) {
    return deskOrderTarget({
      order_id: primary.order_id,
      order_item_id: primary.order_item_id,
      order_no: primary.order_no,
    });
  }
  if (primary.action === 'team') return { name: 'team' };
  // AB#22092 — a bell item from the product development chain opens the record.
  if (primary.action === 'development' && primary.development_id)
    return {
      name: 'product-development',
      query: {
        open: String(primary.development_id),
        ...(primary.step ? { step: String(primary.step) } : {}),
        ...(primary.comment_id ? { comment: String(primary.comment_id) } : {}),
      },
    };
  // AB#22169 — a request: reviewers read it in New requests, its writer in My requests.
  if (primary.action === 'development_request' && primary.request_id)
    return {
      name: primary.view === 'mine' ? 'product-requests' : 'product-development-requests',
      query: { open: String(primary.request_id) },
    };
  // AB#22173 — a notice to acknowledge (or a pattern change to decide on an open order).
  if (primary.action === 'development_notice' && primary.notice_id)
    return { name: 'product-development-notices', query: { open: String(primary.notice_id) } };
  return { name: 'needs-attention' };
};

export const goToDeskTarget = (
  router: { push: (t: unknown) => unknown; resolve: (t: unknown) => { href: string } },
  currentRouteName: unknown,
  target: { name: string; query?: Record<string, string> }
): void => {
  if (target.name === 'dashboard' && currentRouteName === 'dashboard') {
    window.location.assign(router.resolve(target).href);
    return;
  }
  void router.push(target);
};

/** A drawer or a modal is open: the person is mid-task and must not be interrupted. */
export const somethingIsOpen = (): boolean =>
  typeof document !== 'undefined' &&
  document.querySelector('aside.dr, .modal-backdrop, .swal2-container:not(.swal2-toast-shown)') !==
    null;

/** The first placeholder-free preview of a message, for the editor. */
export const previewText = (message: string, sample: Record<string, string | number>): string =>
  message
    .replace(/\{(\w+)\}/g, (m, k) => (k in sample ? String(sample[k]) : m))
    .replace(/\s+/g, ' ')
    .trim();

export const splitHeadline = (text: string): [string, string] => {
  const m = text.match(/^(.*?[.!?])(\s+|$)([\s\S]*)$/);
  if (m && m[1].length <= 140) return [m[1].trim(), m[3].trim()];
  return [text.length > 120 ? text.slice(0, 120) + '…' : text, text.length > 120 ? text : ''];
};
